import React, { useEffect } from 'react';
import Grid from '@/components/molecules/Grid';
import { TextS } from '@/pages/designSystem/Fonts';
import { AddDomainWrap, ExampleDomain } from '@/components/features/widgets/WidgetModalAddYourDomain/styles';
import TextField from '@/components/atoms/UI/forms/TextField/TextField';
import Button from '@/components/atoms/UI/buttons/Button';
import Icon from '@/components/atoms/UI/Icon/Icon';
import DomainItem from '@/components/molecules/DomainItem/DomainItem';
import LoadingErrorHandler from '@/components/templates/LoadingErrorHandler';
import ErrorMessage from '@/components/atoms/UI/forms/ErrorMessage/ErrorMessage';
import { deleteDomainData, fetchDomainsListData, resetError } from '@/store/reducers/domainsListSlice';
import { addDomainFormTypes } from '@/store/types/addDomainFormTypes';
import { sendDomain } from '@/store/reducers/addDomainSlice';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import schemaAddDomain from '@/core/schemes/schemaAddDomain';

import {
	selectAllowDomains,
	selectErrorDomainsList,
	selectIsFetchingDomainsList,
	selectIsNetworkError
} from '@/store/selectors/domainsListSelector';
import { selectDomainFormError, selectInitialDomainState } from '@/store/selectors/addDomainSelectors';
import { AppDispatch } from '@/store/store';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const WidgetAddDomain: React.FC = () => {
	const dispatch: AppDispatch = useDispatch();
	const errorDomainsList = useSelector(selectErrorDomainsList);
	const isFetchingDomainsList = useSelector(selectIsFetchingDomainsList);
	const isNetworkError = useSelector(selectIsNetworkError);
	const error = useSelector(selectDomainFormError);
	const domainList = useSelector(selectAllowDomains);
	const initialState = useSelector(selectInitialDomainState);

	const { register,
		handleSubmit,
		reset,
		formState: { errors, isValid }
	} = useForm<addDomainFormTypes>({
		defaultValues: initialState,
		mode: 'onTouched',
		resolver: yupResolver(schemaAddDomain),
	});

	const { t } = useTranslation();
	const text_description = t('widget.description');
	const text_example = t('widget.example');
	const text_add = t('widget.add');
	const text_add_placeholder = t('widget.add_placeholder');
	const text_maximum_domains = t('widget.maximum_domains');

	const loadData = () => dispatch(fetchDomainsListData());

	const onSubmit = async (data: addDomainFormTypes) => {
		dispatch(resetError());
		await dispatch(sendDomain(data));
		loadData();
		reset();
	};

	useEffect(() => {
		dispatch(resetError());
		loadData();
	}, []);

	const handleDeleteDomain = async (domain: string) => {
		dispatch(resetError());
		const success = await dispatch(deleteDomainData(domain));
		return success || false;
	};

	return (
		<Grid col='1' gap='3' centeredText>
			{(domainList.length < 5) ? <form onSubmit={handleSubmit(onSubmit)}>
				<Grid col='1' gap='3' centeredContent centeredText>
					<TextS>{text_description}</TextS>
					<ExampleDomain>{text_example}:</ExampleDomain>
					<AddDomainWrap gap='1'>
						<TextField
							name='domain'
							placeholder={text_add_placeholder}
							register={register}
							type='text'
							error={errors.domain?.message}
							isRequired
						/>
						<Button light autoWidth type='submit' disabled={!isValid}>
							<Icon name={'Plus'} xSmall /> {text_add}
						</Button>
					</AddDomainWrap>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Grid>
			</form> :
				<TextS>{text_maximum_domains}</TextS>
			}

			{domainList.length > 0 &&
                <Grid col={'1'} gap={'1'}>
					{domainList.map((domain) => (
						<DomainItem
							key={domain}
							domain={domain}
							onDelete={() => handleDeleteDomain(domain)}
							isDeletable={domainList.length > 1}
						/>
					))}
                </Grid>
			}

			<LoadingErrorHandler
				error={errorDomainsList}
				isFetching={isFetchingDomainsList}
				onReload={loadData}
				isNetworkError={isNetworkError}
			/>
		</Grid>
	);
};

export default WidgetAddDomain;
